import LgpdNotice from '@/components/feature/LgpdNotice';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AllFormData {
  eventType: string;
  eventSubtype: string;
  participants: string;
  channels: string;
  city: string;
  venue: string;
  startDate: string;
  endDate: string;
  flexibility: string;
  duration: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

interface Step4Props {
  data: AllFormData;
  onChange: (data: Partial<AllFormData>) => void;
  onBack: () => void;
}

const SUBMIT_URL = 'https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php';

function formatDateBR(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function buildSummaryText(data: AllFormData): string {
  const parts: string[] = [];

  const typeMap: Record<string, string> = {
    corporativo: 'Evento Corporativo',
    congresso: 'Congresso / Conferência',
    feira: 'Feira e Exposição',
    summit: 'Summit / Fórum',
    multilingue: 'Evento Multilíngue',
    outro: 'Outro Tipo',
  };

  parts.push(`Tipo: ${typeMap[data.eventType] || data.eventType}${data.eventSubtype ? ` — ${data.eventSubtype}` : ''}`);
  parts.push(`Participantes: ${data.participants}`);
  parts.push(`Canais simultâneos: ${data.channels}`);
  parts.push(`Cidade: ${data.city}${data.venue ? ` — ${data.venue}` : ''}`);

  if (data.flexibility === 'tbd') {
    parts.push('Data: A definir');
  } else if (data.startDate && data.endDate) {
    parts.push(
      data.startDate === data.endDate
        ? `Data: ${formatDateBR(data.startDate)}`
        : `Período: ${formatDateBR(data.startDate)} a ${formatDateBR(data.endDate)}`
    );
  }

  if (data.duration) parts.push(`Duração por dia: ${data.duration}`);
  if (data.message) parts.push(`Observações: ${data.message}`);

  return parts.join(' | ');
}

export default function Step4Contact({ data, onChange, onBack }: Step4Props) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'error'>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof AllFormData, string>>>({});

  const validate = () => {
    const newErrors: Partial<Record<keyof AllFormData, string>> = {};
    if (!data.name.trim()) newErrors.name = 'Campo obrigatório';
    if (!data.email.trim()) newErrors.email = 'Campo obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) newErrors.email = 'E-mail inválido';
    if (!data.phone.trim()) newErrors.phone = 'Campo obrigatório';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    try {
      const summary = buildSummaryText(data);
      const body = new URLSearchParams();
      body.append('name', data.name);
      body.append('email', data.email);
      body.append('phone', data.phone);
      body.append('company', data.company);
      body.append('eventType', `${data.eventType}${data.eventSubtype ? ` - ${data.eventSubtype}` : ''}`);
      body.append('participants', data.participants);
      body.append('channels', data.channels);
      body.append('city', `${data.city}${data.venue ? ` - ${data.venue}` : ''}`);
      body.append(
        'dates',
        data.flexibility === 'tbd'
          ? 'A definir'
          : data.startDate === data.endDate
          ? formatDateBR(data.startDate)
          : `${formatDateBR(data.startDate)} a ${formatDateBR(data.endDate)}`
      );
      body.append('flexibility', data.flexibility);
      if (data.duration) body.append('duration', data.duration);
      body.append('message', data.message);
      body.append('summary', summary);

      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        navigate('/orcamento/confirmacao', { state: data, replace: true });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 8000);
    }
  };

  const inputClass = (field: keyof AllFormData) =>
    `w-full px-4 py-3 border-2 rounded-xl text-sm font-body text-gray-800 bg-white focus:outline-none transition-colors ${
      errors[field] ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-primary'
    }`;

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary mb-2">
          Seus dados de contato
        </h2>
        <p className="font-body text-gray-500 text-base">
          Quase lá! Informe seus dados para recebermos a proposta personalizada.
        </p>
      </div>

      {/* Summary card */}
      <div className="mb-8 p-5 bg-secondary/5 border border-secondary/10 rounded-2xl">
        <p className="font-body text-xs font-bold uppercase tracking-widest text-secondary/60 mb-3">
          Resumo do pedido
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            {
              icon: 'ri-calendar-event-line',
              label: 'Evento',
              value: `${data.eventSubtype || data.eventType}`,
            },
            { icon: 'ri-group-line', label: 'Participantes', value: data.participants },
            { icon: 'ri-broadcast-line', label: 'Canais', value: `${data.channels} canal(is) simultâneo(s)` },
            { icon: 'ri-map-pin-line', label: 'Cidade', value: `${data.city}${data.venue ? ` — ${data.venue}` : ''}` },
            {
              icon: 'ri-calendar-2-line',
              label: 'Data',
              value:
                data.flexibility === 'tbd'
                  ? 'A definir'
                  : data.startDate === data.endDate
                  ? formatDateBR(data.startDate)
                  : `${formatDateBR(data.startDate)} → ${formatDateBR(data.endDate)}`,
            },
          ].filter(item => item.value).map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-primary text-sm`}></i>
              </div>
              <span className="font-body text-xs text-gray-500">{item.label}:</span>
              <span className="font-body text-xs font-semibold text-secondary truncate">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} data-readdy-form id="orcamento-multistep-form" className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block font-body text-sm font-semibold text-secondary mb-2">
              Nome completo <span className="text-primary">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={data.name}
              onChange={(e) => {
                onChange({ name: e.target.value });
                setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder="Seu nome"
              className={inputClass('name')}
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-secondary mb-2">
              E-mail <span className="text-primary">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={data.email}
              onChange={(e) => {
                onChange({ email: e.target.value });
                setErrors((prev) => ({ ...prev, email: '' }));
              }}
              placeholder="seu@email.com.br"
              className={inputClass('email')}
            />
            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block font-body text-sm font-semibold text-secondary mb-2">
              Telefone / WhatsApp <span className="text-primary">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={data.phone}
              onChange={(e) => {
                onChange({ phone: e.target.value });
                setErrors((prev) => ({ ...prev, phone: '' }));
              }}
              placeholder="(11) 9 9999-9999"
              className={inputClass('phone')}
            />
            {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-secondary mb-2">
              Empresa / Organização
            </label>
            <input
              type="text"
              name="company"
              value={data.company}
              onChange={(e) => onChange({ company: e.target.value })}
              placeholder="Nome da empresa ou entidade"
              className={inputClass('company')}
            />
          </div>
        </div>

        <div>
          <label className="block font-body text-sm font-semibold text-secondary mb-2">
            Informações adicionais (opcional)
          </label>
          <textarea
            name="message"
            value={data.message}
            onChange={(e) => onChange({ message: e.target.value })}
            maxLength={500}
            rows={3}
            placeholder="Requisitos especiais, idiomas necessários, outros detalhes..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-body text-gray-800 bg-white focus:border-primary focus:outline-none transition-colors resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">{data.message.length}/500</p>
        </div>

        {/* Privacy note */}
        <p className="font-body text-xs text-gray-400 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-lock-line text-gray-400 text-xs"></i>
          </span>
          Seus dados são utilizados exclusivamente para enviar a proposta de orçamento. Nunca
          compartilhamos suas informações com terceiros.
        </p>

        {/* Submit error */}
        {submitStatus === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="font-body text-sm text-red-600 font-medium">
              Erro ao enviar. Tente novamente ou entre em contato pelo WhatsApp.
            </p>
          </div>
        )}

        <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            {/* Navigation */}
        <div className="flex items-center gap-4 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-body font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Voltar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enviando...
              </>
            ) : (
              <>
                Enviar Solicitação
                <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-send-plane-line text-white"></i>
                </div>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}